import React, { useState, useCallback } from 'react';
import { X, Play, BookOpen, ChevronRight, Clock } from 'lucide-react';
import { InteractiveDemo } from './InteractiveDemo';
import { GuidedWalkthrough } from './GuidedWalkthrough';

const BRAND = {
  navy: '#0B1F4A',
  blue: '#1A56DB',
  green: '#22c55e',
} as const;

interface DemoSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl?: string;
}

type DemoMode = 'selector' | 'quick' | 'guided';

export const DemoSelector: React.FC<DemoSelectorProps> = ({ isOpen, onClose, videoUrl }) => {
  const [mode, setMode] = useState<DemoMode>('selector');

  const handleClose = useCallback(() => {
    setMode('selector');
    onClose();
  }, [onClose]);

  const handleSwitchToQuick = useCallback(() => {
    setMode('quick');
  }, []);

  const handleSwitchToGuided = useCallback(() => {
    setMode('guided');
  }, []);

  const handleBackToSelector = useCallback(() => {
    setMode('selector');
  }, []);

  if (!isOpen) return null;

  if (mode === 'quick') {
    return (
      <InteractiveDemo 
        isOpen={true} 
        onClose={handleClose}
        videoUrl={videoUrl}
      />
    );
  }

  if (mode === 'guided') {
    return (
      <GuidedWalkthrough 
        isOpen={true} 
        onClose={handleClose}
        onSwitchToQuickDemo={handleSwitchToQuick}
      />
    );
  }

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100" style={{ backgroundColor: BRAND.navy }}>
          <div>
            <h2 className="text-lg font-bold text-white">
              ¿Cómo quieres explorar Clientelo?
            </h2>
            <p className="text-sm text-white/70">Elige tu experiencia · Sin registro</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Options */}
        <div className="p-5 space-y-4">
          {/* Quick Demo Option */}
          <button
            onClick={handleSwitchToQuick}
            className="w-full p-4 rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50/50 transition-all text-left group"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shrink-0 shadow-lg">
                <Play className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                    Demo rápido
                  </h3>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                    <Clock className="w-3 h-3" />
                    2 min
                  </span>
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    Recomendado
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  4 pasos esenciales: crear producto, publicar en tienda, recibir pedido y completar venta.
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-blue-500 shrink-0 mt-1" />
            </div>
          </button>

          {/* Guided Walkthrough Option */}
          <button
            onClick={handleSwitchToGuided}
            className="w-full p-4 rounded-xl border-2 border-gray-200 hover:border-purple-400 hover:bg-purple-50/50 transition-all text-left group"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shrink-0 shadow-lg">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-gray-900 group-hover:text-purple-700 transition-colors">
                    Tour guiado completo
                  </h3>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                    <Clock className="w-3 h-3" />
                    10-15 min
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  7 módulos detallados: Productos, Tienda, Pedidos, Ventas POS, Clientes, Cobros y Check-in QR.
                </p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {['Productos', 'Tienda', 'Pedidos', 'Ventas', 'Clientes', 'Cobros', 'Check-in'].map((m) => (
                    <span key={m} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">
                      {m}
                    </span>
                  ))}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-purple-500 shrink-0 mt-1" />
            </div>
          </button>

          {/* Video Option (if available) */}
          {videoUrl && (
            <a
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full p-4 rounded-xl border-2 border-gray-200 hover:border-green-400 hover:bg-green-50/50 transition-all text-left group flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shrink-0 shadow-lg">
                <Play className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900 group-hover:text-green-700 transition-colors">
                    Ver video comercial
                  </h3>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    <Clock className="w-3 h-3" />
                    1 min
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Mira un resumen de lo que puedes hacer con Clientelo.
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-green-500 shrink-0" />
            </a>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          <button
            onClick={handleClose}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Cerrar
          </button>
          <a
            href="/registro"
            className="text-sm font-medium transition-colors"
            style={{ color: BRAND.blue }}
          >
            ¿Ya convencido? Empieza gratis →
          </a>
        </div>
      </div>
    </div>
  );
};

export default DemoSelector;
